import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { getDarSuspension, createDarSuspension, updateDarSuspension, uploadDarSuspensionCorrigendum, closeDarSuspension, getSapEmployeeFetch, getDarClosed, getDarClosedList } from "../../../core/data/redux/darSlice";
import { getPresignedUrl, uploadFile } from "../../../core/data/redux/awsBucketSlice";
import { AppDispatch } from "../../../core/data/redux/store";
import { CLOSED_CASE_EXACT_ERROR, isClosedStage } from "../../../utils/darValidation";
import "./Dashboard.scss";

const DarSuspension: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const casenoParam = searchParams.get("caseno");
  const isCreate = !casenoParam;
  const todayStr = new Date().toISOString().split("T")[0];

  const [searchCaseNo, setSearchCaseNo] = useState(casenoParam || "");
  const [formData, setFormData] = useState({
    case_no: "",
    empno: "",
    empname: "",
    designation: "",

    susp_type: "Suspended",
    susp_start_date: "",
    subsist_allow: "50",
    order_file: "",
    order_file_url: "",

    corrigendum_order: "",

    revocation_effective_date: "",
    revocation_order: "",

    status: ""
  });

  const [loading, setLoading] = useState(false);
  const [previousData, setPreviousData] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingOrder, setUploadingOrder] = useState(false);
  const [uploadingCorrigendum, setUploadingCorrigendum] = useState(false);
  const [uploadingRevocation, setUploadingRevocation] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const isClosingRef = React.useRef(false);

  const [initiateMode, setInitiateMode] = useState<"case" | "employee">("case");
  const [searchEmpNo, setSearchEmpNo] = useState("");
  const [fetchingEmployee, setFetchingEmployee] = useState(false);
  const [isClosedCase, setIsClosedCase] = useState(false);
  const [checkingCaseStatus, setCheckingCaseStatus] = useState(false);

  const handleSearchCase = async () => {
    const caseNoToSearch = searchCaseNo.trim();
    if (!caseNoToSearch) {
      toast.warning("Please enter a case number.");
      return;
    }
    setCheckingCaseStatus(true);
    try {
      const closedRes = await dispatch(getDarClosed({ caseno: caseNoToSearch })).unwrap();
      const closedStatus = closedRes?.status || closedRes?.data?.status;
      const closedData = closedRes?.data?.data || closedRes?.data;

      if ((closedStatus === 200 || closedStatus === 201) && closedData && (closedData.caseno || closedData.case_no || isClosedStage(closedData))) {
        setIsClosedCase(true);
        setFormData(prev => ({ ...prev, case_no: "" }));
        toast.error(CLOSED_CASE_EXACT_ERROR);
        return;
      }
    } catch (err: any) {
      // Not found in closed cases
    } finally {
      setCheckingCaseStatus(false);
    }

    setIsClosedCase(false);
    setFormData(prev => ({ ...prev, case_no: caseNoToSearch }));
  };

  const handleSearchEmployee = async () => {
    if (!searchEmpNo.trim()) {
      toast.warning("Please enter an Employee Number.");
      return;
    }
    setFetchingEmployee(true);
    try {
      const closedRes = await dispatch(getDarClosedList({ filters: { search_term: searchEmpNo.trim() } })).unwrap();
      const closedItems = closedRes?.data?.data?.items || closedRes?.data?.items;
      if (closedItems && Array.isArray(closedItems) && closedItems.length > 0) {
        const match = closedItems.find((item: any) => {
          const empNo = String(item.empno || item.emp_no || '').trim();
          return empNo === searchEmpNo.trim() || empNo.replace(/^0+/, '') === searchEmpNo.trim().replace(/^0+/, '');
        });
        if (match) {
          setIsClosedCase(true);
          toast.error(CLOSED_CASE_EXACT_ERROR);
          setFetchingEmployee(false);
          return;
        }
      }

      const res = await dispatch(getSapEmployeeFetch({ emp_no: searchEmpNo.trim() })).unwrap();
      if (res?.status === 200 && res?.data) {
        setIsClosedCase(false);
        setFormData(prev => ({
          ...prev,
          empno: res.data.empno || res.data.emp_no || searchEmpNo.trim(),
          empname: res.data.empname || "",
          designation: res.data.designation || "",
          susp_type: "Suspended",
          subsist_allow: "50"
        }));
      } else {
        const errorMsg = res?.message || res?.data?.message || res?.error || "Employee not found.";
        toast.error(errorMsg);
      }
    } catch (err: any) {
      const errorMsg = typeof err === 'string' ? err : (err?.message || err?.error || err?.data?.message || err?.response?.data?.message || "Error fetching employee data.");
      toast.error(errorMsg);
    } finally {
      setFetchingEmployee(false);
    }
  };

  useEffect(() => {
    toast.dismiss();
    if (casenoParam) {
      handleFetchSuspension(casenoParam);
    }
    return () => {
      toast.dismiss();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casenoParam]);

  const extractSubsistAllow = (dataObj: any) => {
    if (!dataObj) return "";
    const raw =
      dataObj.subsist_allow ??
      dataObj.subsistence_allowance ??
      dataObj.subsist_allowance ??
      dataObj.subsistence_allow ??
      dataObj.allowance ??
      dataObj.subsistAllow ??
      dataObj.subsistenceAllowance ??
      dataObj.prev_subsist_allow ??
      dataObj.prev_subsist_allowance ??
      "";

    if (raw !== null && raw !== undefined && String(raw).trim() !== "" && String(raw).trim() !== "null") {
      return String(raw).replace('%', '').trim();
    }
    return "";
  };

  const handleSubsistAllowChange = (val: string) => {
    const numVal = parseFloat(val);
    setFormData(prev => {
      const isDeemed = prev.susp_type === "Deemed Suspension";
      const isZeroToNonZero = isDeemed && !isNaN(numVal) && numVal > 0;
      if (isZeroToNonZero) {
        toast.info("Type of Suspension automatically changed to Suspension from Deemed Suspension.");
      }
      return {
        ...prev,
        subsist_allow: val,
        susp_type: isZeroToNonZero ? "Suspended" : prev.susp_type
      };
    });
  };

  const handleFetchSuspension = async (caseno: string) => {
    setLoading(true);
    try {
      try {
        const closedRes = await dispatch(getDarClosed({ caseno })).unwrap();
        const closedStatus = closedRes?.status || closedRes?.data?.status;
        const closedData = closedRes?.data?.data || closedRes?.data;
        if ((closedStatus === 200 || closedStatus === 201) && closedData && (closedData.caseno || closedData.case_no || isClosedStage(closedData))) {
          setIsClosedCase(true);
          toast.error(CLOSED_CASE_EXACT_ERROR);
        }
      } catch (e) {
        // Not closed
      }

      const response = await dispatch(getDarSuspension({ caseno })).unwrap();
      const status = response?.status || response?.data?.status;
      if ((status === 200 || status === 201) && response?.data) {
        const data = response.data?.data || response.data;
        if (isClosedStage(data)) {
          setIsClosedCase(true);
          toast.error(CLOSED_CASE_EXACT_ERROR);
        }
        const fetchedAllowance = extractSubsistAllow(data) || (data.susp_type === "Deemed Suspension" ? "0" : "50");
        setPreviousData({
          ...data,
          subsist_allow: fetchedAllowance
        });
        setFormData({
          case_no: data.caseno || "",
          empno: data.empno || "",
          empname: data.empname || "",
          designation: data.designation || "",

          susp_type: data.susp_type || "Suspended",
          susp_start_date: data.susp_start_date || "",
          subsist_allow: fetchedAllowance,
          order_file: data.order_file || data.orderfile || "",
          order_file_url: data.urls?.order_file_url || data.urls?.order_url || "",

          corrigendum_order: data.corrigendum_order || data.corrigendumfile || "",

          revocation_effective_date: data.revocation_effective_date || "",
          revocation_order: data.revocation_order || data.revocationfile || "",

          status: data.status || "",
        });

        if (data.order_file && !data.urls?.order_file_url) {
          dispatch(getPresignedUrl(data.order_file))
            .unwrap()
            .then((url: any) => setFormData((prev) => ({ ...prev, order_file_url: url as string })))
            .catch(() => { });
        }
      } else {
        toast.error("Failed to fetch suspension case details.");
      }
    } catch (error: any) {
      toast.error(typeof error === 'string' ? error : (error?.message || "Error fetching suspension case details."));
    } finally {
      setLoading(false);
    }
  };

  const uploadToS3 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = (reader.result as string).split(",")[1];
        try {
          const result = await dispatch(uploadFile({ filestring: base64, mime_type: file.type, module: "dar" }));
          if (uploadFile.fulfilled.match(result)) {
            const key = result.payload?.key || result.payload?.data?.key || result.payload?.url || "";
            if (key) resolve(key);
            else reject(new Error("Failed to retrieve document path"));
          } else {
            reject(new Error("Failed to upload document"));
          }
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, targetField: "order_file" | "corrigendum_order" | "revocation_order") => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB.");
      if (e.target) e.target.value = "";
      return;
    }

    if (file.type !== "application/pdf") {
      toast.error("Invalid file format. Only PDF files are allowed.");
      if (e.target) e.target.value = "";
      return;
    }

    const setUploading = targetField === "order_file" ? setUploadingOrder : targetField === "corrigendum_order" ? setUploadingCorrigendum : setUploadingRevocation;
    setUploading(true);
    const toastId = toast.loading("Uploading document...");
    try {
      const key = await uploadToS3(file);
      setFormData(prev => ({ ...prev, [targetField]: key }));
      toast.dismiss(toastId);
      toast.success("Document uploaded successfully");
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Failed to upload document");
    } finally {
      e.target.value = '';
      setUploading(false);
    }
  };

  const handleRemoveFile = (targetField: "order_file" | "corrigendum_order" | "revocation_order") => {
    setFormData(prev => ({ ...prev, [targetField]: "" }));
  };

  const handleViewFile = async (fileKey: string, existingUrl?: string) => {
    if (existingUrl) {
      window.open(existingUrl, "_blank");
      return;
    }
    const toastId = toast.loading("Fetching document...");
    try {
      const url = await dispatch(getPresignedUrl(fileKey)).unwrap();
      toast.dismiss(toastId);
      window.open(url, "_blank");
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Could not fetch document URL.");
    }
  };

  const isDirectSuspension = formData.susp_type === "Vigilance" || formData.susp_type === "Non-Vigilance";

  const handleSubmit = async (e: React.FormEvent) => {
    const closing = isClosingRef.current;
    console.log("[DarSuspension] handleSubmit fired", { isCreate, initiateMode, closing, formData });
    e.preventDefault();
    if (isCreate && isClosedCase) {
      toast.error(CLOSED_CASE_EXACT_ERROR);
      return;
    }
    if (isCreate && initiateMode === "employee") {
      if (!formData.empno) {
        toast.warning("Please search and select an Employee.");
        return;
      }
    } else {
      if (!formData.case_no) {
        toast.warning("Please provide a Case Number.");
        return;
      }
    }
    if (!closing) {
      const rawAllow = (formData.subsist_allow !== undefined && formData.subsist_allow !== null ? formData.subsist_allow : "").toString().trim();
      if (!rawAllow) {
        toast.warning("Subsistence Allowance (%) is required.");
        return;
      }
      const allowNum = parseFloat(rawAllow);
      const isDeemed = formData.susp_type === "Deemed Suspension";
      const minAllowed = isDeemed ? 0 : 25;
      if (isNaN(allowNum) || allowNum < minAllowed || allowNum > 75) {
        toast.warning(
          isDeemed
            ? "Subsistence Allowance (%) must be between 0 and 75 for Deemed Suspension."
            : "Subsistence Allowance (%) must be in the range of 25 to 75."
        );
        return;
      }
      if (!formData.susp_start_date) {
        toast.warning("Suspension Start Date is required.");
        return;
      }
      if (isCreate && !formData.order_file) {
        toast.warning("Suspension Order File is required.");
        return;
      }
    }
    setSubmitting(true);
    try {
      if (closing) {
        // Handle Close Suspension
        const payload = {
          case_no: formData.case_no,
          revocation_effective_date: formData.revocation_effective_date,
          revocation_order: formData.revocation_order
        };
        const response = await dispatch(closeDarSuspension(payload)).unwrap();
        if (response?.status === 200 || response?.status === 201) {
          toast.success("Suspension case closed successfully!");
          navigate("/dar-cases/sd-suspension");
        } else {
          toast.error("Failed to close suspension case.");
        }
      } else {
        // Handle Create/Update
        // Send subsist_allow as raw trimmed string (backend expects number string e.g. "50" or "50.00")
        const rawAllowVal = (formData.subsist_allow !== undefined && formData.subsist_allow !== null ? formData.subsist_allow : "0").toString().trim();
        const sanitizedAllow = rawAllowVal || "0";
        let response;

        if (isCreate) {
          const payload: any = {
            susp_type: formData.susp_type,
            susp_start_date: formData.susp_start_date,
            subsist_allow: sanitizedAllow,
            order_file: formData.order_file,
          };

          // Detect mode from actual data rather than initiateMode state (avoids stale closure)
          const resolvedEmpNo = formData.empno || searchEmpNo.trim();
          const resolvedCaseNo = formData.case_no;

          if (resolvedEmpNo && !resolvedCaseNo) {
            // Direct Suspension mode — send emp_no
            payload.emp_no = resolvedEmpNo;
          } else if (resolvedCaseNo) {
            // Existing Case mode — send case_no
            payload.case_no = resolvedCaseNo;
          } else {
            toast.warning("Please provide an Employee Number or Case Number.");
            setSubmitting(false);
            return;
          }

          console.log("[DarSuspension] createDarSuspension payload", JSON.stringify(payload));
          response = await dispatch(createDarSuspension(payload)).unwrap();
          console.log("[DarSuspension] createDarSuspension response", JSON.stringify(response));
        } else {
          const payload: any = {
            caseno: formData.case_no,
            susp_type: formData.susp_type,
            susp_start_date: formData.susp_start_date,
            subsist_allow: sanitizedAllow,
            order_file: formData.order_file,
          };
          response = await dispatch(updateDarSuspension(payload)).unwrap();
        }

        const status = response?.status || response?.data?.status || (response?.success ? 200 : null);
        console.log("[DarSuspension] response status check", { status, response });
        if (status === 200 || status === 201) {
          if (!isCreate && formData.corrigendum_order) {
            await dispatch(uploadDarSuspensionCorrigendum({
              caseno: formData.case_no,
              corrigendum_order: formData.corrigendum_order
            })).unwrap();
            toast.success("Suspension case and corrigendum updated successfully!");
          } else {
            toast.success("Suspension case saved successfully!");
          }
          navigate("/dar-cases/sd-suspension");
        } else {
          toast.error("Failed to save suspension case.");
        }
      }
    } catch (error: any) {
      toast.error(typeof error === 'string' ? error : (error?.message || "Error submitting case."));
    } finally {
      setSubmitting(false);
      isClosingRef.current = false;
      setIsClosing(false);
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">
              {isCreate ? "New Suspension Case" : "Edit Suspension Case"}
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to="#">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/dar-cases/sd-suspension">Suspensions</Link></li>
                <li className="breadcrumb-item active" aria-current="page">{isCreate ? "Create" : "Edit"}</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex gap-2">
            <Link to="/dar-cases/sd-suspension" className="btn btn-outline-secondary d-flex align-items-center">
              <i className="ti ti-arrow-left me-2"></i> Back to Overview
            </Link>
          </div>
        </div>

        {isCreate && (
          <div className="card mb-4 border-0 shadow-sm">
            <div className="card-header bg-white border-bottom p-4">
              <h5 className="mb-0 text-primary d-flex align-items-center">
                <i className="ti ti-settings fs-3 me-2"></i> Suspension Case Initiation Mode
              </h5>
              <div className="d-flex gap-4 mt-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="initiateMode"
                    id="modeCase"
                    checked={initiateMode === "case"}
                    onChange={() => {
                      setInitiateMode("case");
                      setIsClosedCase(false);
                      setFormData(prev => ({
                        ...prev,
                        case_no: "",
                        empno: "",
                        empname: "",
                        designation: "",
                        susp_type: "Suspended",
                        subsist_allow: "50"
                      }));
                    }}
                  />
                  <label className="form-check-label" htmlFor="modeCase">
                    For Existing Case (by Case Number)
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="initiateMode"
                    id="modeEmployee"
                    checked={initiateMode === "employee"}
                    onChange={() => {
                      setInitiateMode("employee");
                      setIsClosedCase(false);
                      setFormData(prev => ({
                        ...prev,
                        case_no: "",
                        empno: "",
                        empname: "",
                        designation: "",
                        susp_type: "Suspended",
                        subsist_allow: "50"
                      }));
                    }}
                  />
                  <label className="form-check-label" htmlFor="modeEmployee">
                    Suspension (by Employee Number)
                  </label>
                </div>
              </div>
            </div>

            <div className="card-body p-4 bg-light rounded d-flex gap-3 align-items-end">
              {initiateMode === "case" ? (
                <>
                  <div className="flex-grow-1">
                    <label className="form-label">Search Case Number <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. DAR-2026-HR-008"
                      value={searchCaseNo}
                      onChange={e => {
                        setSearchCaseNo(e.target.value);
                        setIsClosedCase(false);
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary px-4 d-flex align-items-center"
                    disabled={checkingCaseStatus}
                    onClick={handleSearchCase}
                  >
                    {checkingCaseStatus ? (
                      <span className="spinner-border spinner-border-sm me-2"></span>
                    ) : (
                      <i className="ti ti-search me-2"></i>
                    )}
                    Initialize
                  </button>
                </>
              ) : (
                <>
                  <div className="flex-grow-1">
                    <label className="form-label">Search Employee Number <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. 00020990"
                      value={searchEmpNo}
                      onChange={e => {
                        setSearchEmpNo(e.target.value);
                        setIsClosedCase(false);
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary px-4 d-flex align-items-center"
                    disabled={fetchingEmployee}
                    onClick={handleSearchEmployee}
                  >
                    {fetchingEmployee ? (
                      <span className="spinner-border spinner-border-sm me-2"></span>
                    ) : (
                      <i className="ti ti-search me-2"></i>
                    )}
                    Search & Initialize
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {isClosedCase && (
          <div className="alert alert-danger d-flex align-items-center mb-4 shadow-sm" role="alert">
            <i className="ti ti-alert-triangle-filled me-2 fs-4"></i>
            <div>
              <strong>Validation Error:</strong> {CLOSED_CASE_EXACT_ERROR}
            </div>
          </div>
        )}

        {((initiateMode === "case" && formData.case_no) || (initiateMode === "employee" && formData.empno) || !isCreate) && (
          <form onSubmit={handleSubmit} noValidate>
            {!isCreate && previousData && (
              <div className="card shadow-sm border-0 border-start border-primary border-4 mb-4">
                <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-primary fw-bold d-flex align-items-center">
                    <i className="ti ti-history me-2 fs-4"></i> Previous / Current Suspension Details
                  </h5>
                  <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2">
                    Existing Record
                  </span>
                </div>
                <div className="card-body p-4 bg-light-subtle">
                  <div className="row g-4">
                    <div className="col-md-3">
                      <div className="text-muted small fw-medium mb-1">Previous Suspension Type</div>
                      <div className="fw-semibold text-dark">
                        {previousData.susp_type || previousData.suspension_type || "-"}
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-muted small fw-medium mb-1">Previous Start Date</div>
                      <div className="fw-semibold text-dark">
                        {previousData.susp_start_date ? new Date(previousData.susp_start_date).toLocaleDateString("en-GB", { day: '2-digit', month: 'short', year: 'numeric' }) : "-"}
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-muted small fw-medium mb-1">Previous Subsistence Allowance</div>
                      <div className="fw-semibold text-dark">
                        {previousData.subsist_allow ? `${String(previousData.subsist_allow).replace('%', '')}%` : "-"}
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-muted small fw-medium mb-1">Current Status</div>
                      <div>
                        <span className={`badge ${previousData.status === 'Closed' ? 'bg-success' : 'bg-warning text-dark'} px-2 py-1`}>
                          {previousData.status || "Active"}
                        </span>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="text-muted small fw-medium mb-1">Existing Suspension Order</div>
                      {(previousData.order_file || previousData.orderfile || previousData.urls?.order_file_url || formData.order_file_url) ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary mt-1 d-flex align-items-center gap-2"
                          onClick={() => handleViewFile(previousData.order_file || previousData.orderfile, previousData.urls?.order_file_url || formData.order_file_url)}
                        >
                          <i className="ti ti-file-text"></i> View Existing Suspension Order
                        </button>
                      ) : (
                        <span className="text-muted small fst-italic">No Order Attached</span>
                      )}
                    </div>

                    <div className="col-md-6">
                      <div className="text-muted small fw-medium mb-1">Existing Corrigendum Order</div>
                      {(previousData.corrigendum_order || previousData.corrigendumfile || previousData.urls?.corrigendum_url) ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary mt-1 d-flex align-items-center gap-2"
                          onClick={() => handleViewFile(previousData.corrigendum_order || previousData.corrigendumfile, previousData.urls?.corrigendum_url)}
                        >
                          <i className="ti ti-file-symlink"></i> View Existing Corrigendum
                        </button>
                      ) : (
                        <span className="text-muted small fst-italic">No Corrigendum Attached</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!isCreate && previousData && previousData.previous_suspensions && previousData.previous_suspensions.length > 0 && (
              <div className="card shadow-sm border-0 border-start border-secondary border-4 mb-4">
                <div className="card-header bg-white border-bottom p-3">
                  <h5 className="mb-0 text-secondary fw-bold d-flex align-items-center">
                    <i className="ti ti-history me-2 fs-4"></i> Previous Actions
                  </h5>
                </div>
                <div className="card-body p-4 bg-light-subtle">
                  <div className="table-responsive">
                    <table className="table table-hover border bg-white">
                      <thead className="table-light">
                        <tr>
                          <th>S.No.</th>
                          <th>Suspension No</th>
                          <th>Type</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th>Allowance</th>
                          <th>Documents</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previousData.previous_suspensions.map((prev: any, index: number) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>{prev.suspension_no || "-"}</td>
                            <td>{prev.susp_type || "-"}</td>
                            <td>{prev.susp_start_date ? new Date(prev.susp_start_date).toLocaleDateString("en-GB", { day: '2-digit', month: 'short', year: 'numeric' }) : "-"}</td>
                            <td>{prev.susp_end_date ? new Date(prev.susp_end_date).toLocaleDateString("en-GB", { day: '2-digit', month: 'short', year: 'numeric' }) : "-"}</td>
                            <td>{prev.subsist_allow !== null && prev.subsist_allow !== undefined ? `${String(prev.subsist_allow).replace('%', '')}%` : "-"}</td>
                            <td>
                              <div className="d-flex gap-2">
                                {(prev.urls?.order_url || prev.orderfile) && (
                                  <button
                                    type="button"
                                    onClick={() => handleViewFile(prev.urls?.order_url || prev.orderfile, prev.urls?.order_url)}
                                    className="btn btn-sm btn-outline-primary"
                                    title="View Order"
                                  >
                                    <i className="ti ti-file-text"></i> Order
                                  </button>
                                )}
                                {(prev.urls?.revocation_url || prev.revocationfile) && (
                                  <button
                                    type="button"
                                    onClick={() => handleViewFile(prev.urls?.revocation_url || prev.revocationfile, prev.urls?.revocation_url)}
                                    className="btn btn-sm btn-outline-success"
                                    title="View Revocation"
                                  >
                                    <i className="ti ti-file-check"></i> Revocation
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            <div className="card shadow-sm border-0 mb-4">
              <div className="card-header bg-white border-bottom p-4">
                <h5 className="mb-0 text-primary d-flex align-items-center">
                  <i className="ti ti-gavel fs-3 me-2" style={{ fontSize: '1.5rem' }}></i> Suspension Details
                </h5>
              </div>
              <div className="card-body p-4">
                <div className="row g-4">
                  {(formData.case_no || (!isCreate && !isDirectSuspension)) && (
                    <div className="col-md-4">
                      <label className="form-label">Case Number <span className="text-danger">*</span></label>
                      <input type="text" className="form-control bg-light" value={formData.case_no} readOnly />
                    </div>
                  )}

                  {(initiateMode === "employee" || isDirectSuspension) && (
                    <>
                      <div className="col-md-4">
                        <label className="form-label">Employee Number</label>
                        <input type="text" className="form-control bg-light" value={formData.empno} readOnly />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Employee Name</label>
                        <input type="text" className="form-control bg-light" value={formData.empname} readOnly />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Designation</label>
                        <input type="text" className="form-control bg-light" value={formData.designation} readOnly />
                      </div>
                    </>
                  )}

                  <div className="col-md-4">
                    <label className="form-label">Type of Suspension <span className="text-danger">*</span></label>
                    <select
                      className="form-select"
                      value={formData.susp_type}
                      onChange={e => {
                        const newType = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          susp_type: newType,
                          subsist_allow: newType === "Deemed Suspension" ? "0" : (prev.subsist_allow === "0" || !prev.subsist_allow ? "50" : prev.subsist_allow)
                        }));
                      }}
                      required
                    >
                      <option value="Suspended">Suspended</option>
                      <option value="Deemed Suspension">Deemed Suspension</option>
                      {formData.susp_type === "Vigilance" && <option value="Vigilance">Vigilance</option>}
                      {formData.susp_type === "Non-Vigilance" && <option value="Non-Vigilance">Non-Vigilance</option>}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Suspension Start Date <span className="text-danger">*</span></label>
                    <input type="date" className="form-control" value={formData.susp_start_date || ""} onChange={e => setFormData({ ...formData, susp_start_date: e.target.value })} required />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Subsistence Allowance (%) <span className="text-danger">*</span></label>
                    {isCreate ? (
                      <>
                        <input
                          type="number"
                          className="form-control"
                          min={0}
                          max={100}
                          step={0.01}
                          value={formData.subsist_allow}
                          onChange={e => handleSubsistAllowChange(e.target.value)}
                          placeholder={formData.susp_type === "Deemed Suspension" ? "0" : "50"}
                          required
                        />
                        <small className="text-muted">
                          {formData.susp_type === "Deemed Suspension" ? "Default is 0% for Deemed Suspension" : "Default is 50% for Suspension"}
                        </small>
                      </>
                    ) : (
                      <select
                        className="form-select"
                        value={String(formData.subsist_allow).replace('%', '').trim()}
                        onChange={e => handleSubsistAllowChange(e.target.value)}
                        required
                      >
                        {(formData.susp_type === "Deemed Suspension" || String(formData.subsist_allow).replace('%', '').trim() === "0") && (
                          <option value="0">0%</option>
                        )}
                        <option value="25">25%</option>
                        <option value="50">50%</option>
                        <option value="75">75%</option>
                        {formData.subsist_allow && !["0", "25", "50", "75"].includes(String(formData.subsist_allow).replace('%', '').trim()) && (
                          <option value={String(formData.subsist_allow).replace('%', '').trim()}>
                            {formData.subsist_allow}%
                          </option>
                        )}
                      </select>
                    )}
                  </div>

                  {isCreate ? (
                    <div className="col-md-12">
                      <label className="form-label">
                        Suspension Order File <span className="text-danger">*</span>
                      </label>
                      <div className="d-flex align-items-center gap-3">
                        <input type="file" id="orderDoc" className="d-none" accept=".pdf" onChange={e => handleFileUpload(e, "order_file")} disabled={uploadingOrder} />
                        <button type="button" className="btn btn-outline-warning text-warning border-warning d-flex align-items-center gap-2 bg-white" onClick={() => document.getElementById("orderDoc")?.click()} disabled={uploadingOrder}>
                          <i className="ti ti-upload"></i> {uploadingOrder ? "Uploading..." : "Upload Order"}
                        </button>
                        {formData.order_file && !uploadingOrder && (
                          <div className="d-flex align-items-center gap-2">
                            <button type="button" className="btn btn-link p-0 m-0 text-success text-decoration-none" onClick={() => handleViewFile(formData.order_file, formData.order_file_url)}>
                              {formData.order_file.split('/').pop()}
                            </button>
                            <button type="button" className="btn btn-sm btn-icon btn-outline-danger" onClick={() => handleRemoveFile("order_file")}>
                              <i className="ti ti-trash"></i>
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="text-muted small mt-2">Only PDF files under 5MB are acceptable.</div>
                    </div>
                  ) : (
                    <>
                      <div className="col-md-6">
                        <label className="form-label">Attached Order File</label>
                        <div className="d-flex align-items-center gap-3 mt-1">
                          <input type="file" id="orderDoc" className="d-none" accept=".pdf" onChange={e => handleFileUpload(e, "order_file")} disabled={uploadingOrder} />
                          <button type="button" className="btn btn-outline-warning text-warning border-warning d-flex align-items-center gap-2 bg-white" onClick={() => document.getElementById("orderDoc")?.click()} disabled={uploadingOrder}>
                            <i className="ti ti-upload"></i> {uploadingOrder ? "Uploading..." : "Update Order"}
                          </button>
                          {formData.order_file && !uploadingOrder && (
                            <div className="d-flex align-items-center gap-2">
                              <button type="button" className="btn btn-link p-0 m-0 text-success text-decoration-none" onClick={() => handleViewFile(formData.order_file, formData.order_file_url)}>
                                {formData.order_file.split('/').pop()}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Upload Corrigendum (Optional)</label>
                        <div className="d-flex align-items-center gap-3 mt-1">
                          <input type="file" id="corrigendumDoc" className="d-none" accept=".pdf" onChange={e => handleFileUpload(e, "corrigendum_order")} disabled={uploadingCorrigendum} />
                          <button type="button" className="btn btn-outline-warning text-warning border-warning d-flex align-items-center gap-2 bg-white" onClick={() => document.getElementById("corrigendumDoc")?.click()} disabled={uploadingCorrigendum}>
                            <i className="ti ti-upload"></i> {uploadingCorrigendum ? "Uploading..." : "Upload Corrigendum"}
                          </button>
                          {formData.corrigendum_order && !uploadingCorrigendum && (
                            <div className="d-flex align-items-center gap-2">
                              <button type="button" className="btn btn-link p-0 m-0 text-success text-decoration-none" onClick={() => handleViewFile(formData.corrigendum_order)}>
                                {formData.corrigendum_order.split('/').pop()}
                              </button>
                              <button type="button" className="btn btn-sm btn-icon btn-outline-danger" onClick={() => handleRemoveFile("corrigendum_order")}>
                                <i className="ti ti-trash"></i>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="card-footer bg-white border-top p-3 text-end">
                <button type="submit" onClick={() => { isClosingRef.current = false; setIsClosing(false); }} className="btn btn-warning px-4 text-dark" disabled={submitting || (isCreate && isClosedCase)}>
                  {submitting && !isClosing ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="ti ti-device-floppy me-2"></i>}
                  {isCreate ? "Create Suspension" : "Update Details"}
                </button>
              </div>
            </div>

            {!isCreate && (
              <div className="card shadow-sm border-0 border-danger border-top border-3">
                <div className="card-header bg-white border-bottom p-4">
                  <h5 className="mb-0 text-danger d-flex align-items-center">
                    <i className="ti ti-gavel me-2" style={{ fontSize: '1.5rem' }}></i> Revocation
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="row g-4">
                    <div className="col-md-6">
                      <label className="form-label">Revocation Effective Date <span className="text-danger">*</span></label>
                      <input type="date" className="form-control" value={formData.revocation_effective_date || ""} onChange={e => setFormData({ ...formData, revocation_effective_date: e.target.value })} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        Revocation Order File <span className="text-danger">*</span>
                      </label>
                      <div className="d-flex align-items-center gap-3">
                        <input type="file" id="revocationDoc" className="d-none" accept=".pdf" onChange={e => handleFileUpload(e, "revocation_order")} disabled={uploadingRevocation} />
                        <button type="button" className="btn btn-outline-warning text-warning border-warning d-flex align-items-center gap-2 bg-white" onClick={() => document.getElementById("revocationDoc")?.click()} disabled={uploadingRevocation}>
                          <i className="ti ti-upload"></i> {uploadingRevocation ? "Uploading..." : "Upload Revocation Order"}
                        </button>
                        {formData.revocation_order && !uploadingRevocation && (
                          <div className="d-flex align-items-center gap-2">
                            <button type="button" className="btn btn-link p-0 m-0 text-success text-decoration-none" onClick={() => handleViewFile(formData.revocation_order)}>
                              {formData.revocation_order.split('/').pop()}
                            </button>
                            <button type="button" className="btn btn-sm btn-icon btn-outline-danger" onClick={() => handleRemoveFile("revocation_order")}>
                              <i className="ti ti-trash"></i>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-footer bg-white border-top p-3 text-end">
                  <button type="submit" onClick={() => { isClosingRef.current = true; setIsClosing(true); }} className="btn btn-danger px-4" disabled={submitting || !formData.revocation_effective_date || !formData.revocation_order}>
                    {submitting && isClosing ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="ti ti-square-rounded-x me-2"></i>}
                    Close Suspension Case
                  </button>
                </div>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default DarSuspension;
